import React, { useState, useEffect } from 'react';
import type { PlanStep, EmailContent, SMSContent, TrackerComContent } from '../../types/agent.types';
import { apiClient } from '@/core/api/client';

const TEAM_MEMBERS = [
  { name: 'Frédéric Chatelain', email: 'frederic.chatelain@chatelainxpert.be' },
  { name: 'Caroline Degrange', email: 'caroline.degrange@chatelainxpert.be' },
  { name: 'Florine Lambert', email: 'florine.lambert@chatelainxpert.be' },
  { name: 'Laura Farrauto', email: 'laura.farrauto@chatelainxpert.be' },
  { name: 'Florine Andre', email: 'florine.andre@chatelainxpert.be' },
  { name: 'Coralie Dedobbeleer', email: 'coralie.dedobbeleer@chatelainxpert.be' },
  { name: 'Manon Degeye', email: 'manon.degeye@chatelainxpert.be' },
  { name: 'Morgane Metzmacher', email: 'morgane.metzmacher@chatelainxpert.be' },
  { name: 'Alexandre Odekerken', email: 'alexandre.odekerken@chatelainxpert.be' },
  { name: 'Mathilde Bertrand', email: 'mathilde.bertrand@chatelainxpert.be' },
  { name: 'Charlotte Praillet', email: 'charlotte.praillet@chatelainxpert.be' },
  { name: 'Clément Danze', email: 'stagiaire@chatelainxpert.be' },
];

/** Match a team member name to a dossier role person (e.g. "Florine Andre" to "F ANDRE") */
function matchPerson(memberName: string, person?: string): boolean {
  if (!person) return false;
  const mParts = memberName.toLowerCase().split(' ');
  const pParts = person.toLowerCase().trim().split(/\s+/);
  const mLast = mParts[mParts.length - 1] || '';
  const pLast = pParts[pParts.length - 1] || '';
  // Exact full name match
  if (memberName.toLowerCase() === person.toLowerCase().trim()) return true;
  // Last name exact match (most reliable)
  if (mLast.length >= 3 && mLast === pLast) return true;
  return false;
}

/** Sort team members: RV first, then IN, then rest. Dossier-specific priority. */
function sortTeamByRoles(members: typeof TEAM_MEMBERS, roles?: StepPreviewProps['affaireRoles']) {
  if (!roles) return members;
  return [...members].sort((a, b) => {
    const aScore = matchPerson(a.name, roles.rv) ? 0 : matchPerson(a.name, roles.in) ? 1 : 2;
    const bScore = matchPerson(b.name, roles.rv) ? 0 : matchPerson(b.name, roles.in) ? 1 : 2;
    return aScore - bScore;
  });
}

/** Safety net: extract the longest string from raw step content when normalized fields are empty */
function findLongestStringInContent(content: unknown): string {
  if (!content || typeof content !== 'object') return '';
  let best = '';
  for (const val of Object.values(content as Record<string, unknown>)) {
    if (typeof val === 'string' && val.length > best.length) {
      best = val;
    }
  }
  return best;
}

interface StepPreviewProps {
  step: PlanStep;
  onAssignChange?: (email: string) => void;
  affaireRoles?: { rv?: string; in?: string; eval1?: string; eval2?: string; eval3?: string };
  affaireSnapshot?: Record<string, unknown>;
}

export const StepPreview: React.FC<StepPreviewProps> = ({ step, onAssignChange, affaireRoles, affaireSnapshot }) => {
  const content = step.modified_content || step.content;

  // Resolve template for email preview
  const [resolvedEmail, setResolvedEmail] = useState<{ subject: string; body: string; signer: string } | null>(null);
  const email = step.type === 'email' ? content as EmailContent | undefined : undefined;

  useEffect(() => {
    if (!email?.template_id) {
      setResolvedEmail(null);
      return;
    }
    apiClient.post('/api/admin/agent/templates/resolve', {
      template_id: email.template_id,
      variables: email.variables || {},
      language: email.language || (affaireSnapshot as Record<string, string>)?.client_language || 'FR',
      snapshot: affaireSnapshot || {},
    }).then((res: any) => {
      if (res?.success) setResolvedEmail(res.data);
      else if (res?.data?.success) setResolvedEmail(res.data.data); // fallback
    }).catch(() => {});
  }, [email?.template_id, JSON.stringify(email?.variables)]);

  if (step.type === 'email') {
    if (!email) return null;
    const displaySubject = email.subject || resolvedEmail?.subject || '';
    const displayBody = email.body || resolvedEmail?.body || '';
    const templateBadge = email.template_id
      ? <span className="agent-preview-template-badge">{email.template_id}{resolvedEmail?.signer === 'fch' ? ' (FCH)' : ''}</span>
      : null;
    const isPaymentTemplate = email.template_id && (
      email.template_id.startsWith('rappel_paiement_') ||
      email.template_id.startsWith('rappel_post_rapport_') ||
      email.template_id === 'escalade_impaye'
    );
    const willAttachInvoice = email.attach_invoice || isPaymentTemplate;

    return (
      <div className="agent-step-preview">
        <div className="agent-preview-title">
          Aperçu de l'email
          {templateBadge}
        </div>
        <div className="agent-preview-field">
          <span className="agent-preview-label">À:</span>
          <span className="agent-preview-value">{email.to}</span>
        </div>
        {email.cc && (
          <div className="agent-preview-field">
            <span className="agent-preview-label">CC:</span>
            <span className="agent-preview-value">{email.cc}</span>
          </div>
        )}
        {willAttachInvoice && (
          <div className="agent-preview-field">
            <span className="agent-preview-label">PJ:</span>
            <span className="agent-preview-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef5343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
              <span style={{ color: '#ef5343', fontWeight: 500 }}>Facture PDF (auto-jointe)</span>
            </span>
          </div>
        )}
        <div className="agent-preview-field">
          <span className="agent-preview-label">Objet:</span>
          <span className="agent-preview-value">{displaySubject}</span>
        </div>
        <hr className="agent-preview-separator" />
        <div className="agent-preview-body">{displayBody || <em style={{ color: '#9ca3af' }}>Chargement du template...</em>}</div>
      </div>
    );
  }

  if (step.type === 'sms') {
    const sms = content as SMSContent | undefined;
    if (!sms) return null;
    const message = sms.message || '';
    const charCount = message.length;
    const smsCount = Math.ceil(charCount / 160);
    let charCountClass = 'agent-preview-char-count';
    if (charCount > 160) charCountClass += ' agent-preview-char-count--danger';
    else if (charCount > 140) charCountClass += ' agent-preview-char-count--warning';

    return (
      <div className="agent-step-preview">
        <div className="agent-preview-title">Aperçu du SMS</div>
        <div className="agent-preview-field">
          <span className="agent-preview-label">À:</span>
          <span className="agent-preview-value">
            {sms.to}
            {sms.recipient_name && ` (${sms.recipient_name}${sms.recipient_role ? ` - ${sms.recipient_role}` : ''})`}
          </span>
        </div>
        <hr className="agent-preview-separator" />
        <div className="agent-preview-body">{message || <em style={{ color: '#9ca3af' }}>Contenu genere par le template</em>}</div>
        <div className={charCountClass}>
          <svg className="agent-preview-char-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>
          {charCount} / 160 caracteres ({smsCount} SMS)
        </div>
      </div>
    );
  }

  if (step.type === 'tracker_com') {
    const tracker = content as TrackerComContent | undefined;
    if (!tracker) return null;
    const message = tracker.message || findLongestStringInContent(content);
    const assignTo = tracker.assign_to || '';
    const currentMember = TEAM_MEMBERS.find(m => m.email === assignTo);
    const displayName = currentMember?.name || assignTo;

    return (
      <div className="agent-step-preview">
        <div className="agent-preview-title">Aperçu communication tracker</div>
        {tracker.affaire_id && (
          <div className="agent-preview-field">
            <span className="agent-preview-label">Affaire:</span>
            <span className="agent-preview-value">#{tracker.affaire_id}</span>
          </div>
        )}
        <div className="agent-preview-field">
          <span className="agent-preview-label">Assigné à:</span>
          <span className="agent-preview-value agent-preview-assign">
            {onAssignChange ? (
              <select
                className="agent-assign-select"
                value={assignTo}
                onChange={(e) => onAssignChange(e.target.value)}
              >
                {!assignTo && <option value="">-- Choisir --</option>}
                {sortTeamByRoles(TEAM_MEMBERS, affaireRoles).map(m => {
                  const roles: string[] = [];
                  if (matchPerson(m.name, affaireRoles?.rv)) roles.push('RV');
                  if (matchPerson(m.name, affaireRoles?.in)) roles.push('IN');
                  if (matchPerson(m.name, affaireRoles?.eval1)) roles.push('Eval 1');
                  if (matchPerson(m.name, affaireRoles?.eval2)) roles.push('Eval 2');
                  if (matchPerson(m.name, affaireRoles?.eval3)) roles.push('Eval 3');
                  const tag = roles.length > 0 ? `  ★ ${roles.join(', ')}` : '';
                  return <option key={m.email} value={m.email}>{m.name}{tag}</option>;
                })}
              </select>
            ) : (
              displayName || <em style={{ color: '#ef4444' }}>Non assigné</em>
            )}
          </span>
        </div>
        <div className="agent-preview-field">
          <span className="agent-preview-label">Urgence:</span>
          <span className="agent-preview-value">{tracker.urgency || 'medium'}</span>
        </div>
        <hr className="agent-preview-separator" />
        <div className="agent-preview-body">{message || <em style={{ color: '#9ca3af' }}>Pas de contenu</em>}</div>
      </div>
    );
  }

  return null;
};
